import StudentData from "../components/StudentData";
import Loading from "../components/Loading";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { bulkForward } from "../actions/studensts";

const NEXT_STATE_MAP = {
  "auditing/new":            { label: "Invoice",          to: "invoice" },
  "auditing/send":           { label: "Invoice",          to: "invoice" },
  "auditing/edit":           { label: "Invoice",          to: "invoice" },
  "auditing/request_edit":   { label: "Invoice",          to: "invoice" },
  "invoice/new":             { label: "Print",            to: "print" },
  "invoice/wait":            { label: "Print",            to: "print" },
  "invoice/another_invoice": { label: "Print",            to: "print" },
  "score/score_validation":  { label: "Score Request",    to: "score_request" },
  "score/new":               { label: "Score Pending",    to: "score_pending" },
  "score/score_pending":     { label: "MOE Validation",   to: "moe_validation" },
  "print/new":               { label: "Print Validation", to: "print_validation" },
  "print/print_validation":  { label: "Score Validation", to: "score_validation" },
  "moe/moe_validation":      { label: "MOE",              to: "moe" },
  "moe/new":                 { label: "MOE Pending",       to: "moe_pending" },
  "moe/moe_pending":         { options: [
                                { label: "Delivery Hold", to: "delivery_hold" },
                                { label: "AATC Review",   to: "aatc_review" },
                              ]},
  "deliver/new":             { label: "Done",             to: "done" },
  "deliver/aramex":          { label: "Done",             to: "done" },
  "mohe/mohe_validation":    { label: "MOHE",             to: "mohe" },
  "mohe/new":                { label: "Done",             to: "done" },
};

export default function StudentList({
  setSearch,
  search,
  students,
  loading,
  count,
}) {
  const user = JSON.parse(localStorage.getItem("profile")).value[0];
  const { username, role } = user;
  const canBulkSelect =
    username === "admin" || username === "asmaa" || username === "abdelrhman";

  const location = useLocation();
  const [localSearch, setLocalSearch] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const section = location.pathname.split("/")[2];
  const sub = location.pathname.split("/")[3];
  const nextStateInfo = NEXT_STATE_MAP[`${section}/${sub}`] || null;

  useEffect(() => {
    setSelectedIds(new Set());
    setBulkResult(null);
  }, [location.pathname]);

  // Restore the bulk-forward toast after the page reload triggered by handleBulkForward
  useEffect(() => {
    const stored = sessionStorage.getItem("bulkForwardToast");
    if (stored) {
      try {
        setBulkResult(JSON.parse(stored));
      } catch {
        /* ignore malformed payload */
      }
      sessionStorage.removeItem("bulkForwardToast");
      const t = setTimeout(() => setBulkResult(null), 4000);
      return () => clearTimeout(t);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(localSearch);
    setHasSearched(true);
    setSelectedIds(new Set());
    setBulkResult(null);
  };

  const BULK_LIMIT = 100;

  const handleToggle = (id) => {
    setSelectedIds((prev) => {
      if (!prev.has(id) && prev.size >= BULK_LIMIT) return prev;
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!students) return;
    if (selectedIds.size > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(students.slice(0, BULK_LIMIT).map((s) => s.id)));
    }
  };

  const handleBulkForward = async (target) => {
    if (!target || selectedIds.size === 0) return;
    setBulkLoading(true);
    setBulkResult(null);
    const ids = Array.from(selectedIds);
    const res = await bulkForward(target.to, ids, username);
    setBulkLoading(false);
    if (res?.status === 200) {
      const { succeeded, failed, total } = res.data;
      setBulkResult({
        type: "success",
        message: `✓ ${succeeded}/${total} students moved to "${target.label}"${failed > 0 ? ` (${failed} failed)` : ""}.`,
      });
      setSelectedIds(new Set());
      sessionStorage.setItem(
        "bulkForwardToast",
        JSON.stringify({
          type: "success",
          message: `✓ ${succeeded}/${total} students moved to "${target.label}"${failed > 0 ? ` (${failed} failed)` : ""}.`,
        })
      );
      setTimeout(() => window.location.reload(), 1200);
    } else {
      setBulkResult({
        type: "error",
        message: "Bulk forward failed. Please try again.",
      });
    }
  };

  const allSelected =
    students && students.length > 0 &&
    (selectedIds.size === Math.min(students.length, BULK_LIMIT));

  return (
    <div className="flex flex-col items-center">
      {count && user.role !== "user" ? (
        <span
          className="mt-0 mb-5 p-3 w-14 h-14 flex items-center justify-center 
      rounded-full text-sm text-green-700 bg-green-100"
        >
          {students?.length}
        </span>
      ) : (
        ""
      )}
      <form className="flex justify-between" onSubmit={handleSearch}>
        <div>
          <input
            type="search"
            className="bg-white border-2 h-10 pr-5 pl-10 rounded-full text-sm 
                      outline-2 outline-blue-700"
            placeholder="Search by National ID, Passport, or Name..."
            onChange={(e) => setLocalSearch(e.target.value)}
            value={localSearch}
          />
          <button
            type="submit"
            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded"
          >
            Search
          </button>
        </div>
      </form>

      {bulkResult && (
        <div
          className={`mt-3 w-full px-4 py-2 rounded-lg text-sm font-medium ${
            bulkResult.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {bulkResult.message}
        </div>
      )}

      {hasSearched ? (
        loading ? (
          <Loading />
        ) : (
          <>
            {canBulkSelect && nextStateInfo && students?.length > 0 && (
              <div className="w-full flex items-center gap-3 mt-3 mb-1 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="select-all-cb"
                  className="w-4 h-4 accent-blue-600 cursor-pointer"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <label
                  htmlFor="select-all-cb"
                  className="text-sm text-blue-800 cursor-pointer select-none"
                >
                  {selectedIds.size === 0
                    ? `Select all${students.length > BULK_LIMIT ? ` (first ${BULK_LIMIT})` : ` (${students.length})`}`
                    : `${selectedIds.size} of ${Math.min(students.length, BULK_LIMIT)} selected`}
                </label>
              </div>
            )}
            <StudentData
              students={students}
              selectedIds={canBulkSelect && nextStateInfo ? selectedIds : null}
              onToggle={canBulkSelect && nextStateInfo ? handleToggle : null}
            />
          </>
        )
      ) : (
        <div className="text-gray-500 mt-8">Please enter a search term to view data or press search without entering any data to show all students in this state.</div>
      )}

      {/* Floating bulk action bar */}
      {canBulkSelect && selectedIds.size > 0 && nextStateInfo && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-blue-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 whitespace-nowrap">
          <span className="font-semibold">{selectedIds.size} selected</span>
          {nextStateInfo.options ? (
            <>
              <span className="text-blue-400 text-lg">→</span>
              {nextStateInfo.options.map((opt) => (
                <button
                  key={opt.to}
                  onClick={() => handleBulkForward(opt)}
                  disabled={bulkLoading}
                  className="bg-white text-blue-900 font-semibold px-4 py-1.5 rounded-lg text-sm hover:bg-blue-50 transition disabled:opacity-50 cursor-pointer"
                >
                  {bulkLoading ? "Moving..." : opt.label}
                </button>
              ))}
            </>
          ) : (
            <>
              <span className="text-blue-400 text-lg">→</span>
              <span className="text-blue-200 text-sm">{nextStateInfo.label}</span>
              <button
                onClick={() => handleBulkForward(nextStateInfo)}
                disabled={bulkLoading}
                className="bg-white text-blue-900 font-semibold px-4 py-1.5 rounded-lg text-sm hover:bg-blue-50 transition disabled:opacity-50 cursor-pointer"
              >
                {bulkLoading ? "Moving..." : "Move to Next State"}
              </button>
            </>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-blue-300 hover:text-white transition text-xl leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

